import { useState, type ReactNode } from 'react';
import { Eye, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DocumentViewer } from '@/components/DocumentViewer';
import { formatFileName } from '@/lib/utils';

interface PdfDocumentRowProps {
  name: string;
  url?: string;
  caption: string;
  /** Replaces the default View button, e.g. a Remove button while uploading. */
  action?: ReactNode;
}

/** A stored PDF (loan agreement, closure document) with a View button. */
export function PdfDocumentRow({ name, url, caption, action }: PdfDocumentRowProps) {
  const [viewing, setViewing] = useState(false);

  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <span className="flex h-10 w-9 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-700">
        <FileText className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-gray-900" title={name}>
          {formatFileName(name)}
        </p>
        <p className="text-xs text-gray-500">{caption}</p>
      </div>
      {action ?? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 border-gray-200 bg-white"
          disabled={!url}
          onClick={() => setViewing(true)}
        >
          <Eye className="mr-1.5 h-3.5 w-3.5" />
          View
        </Button>
      )}

      <DocumentViewer
        isOpen={viewing}
        onClose={() => setViewing(false)}
        documentName={name}
        documentUrl={url}
        title={caption}
      />
    </div>
  );
}
